import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import type { FilterValues } from "@/lib/filters";
import { FreshnessLine } from "@/components/shell/FreshnessLine";
import { AlertCard } from "@/modules/protex/components/AlertCard";

import { getWeek, getWeeks, messageFor, weekKey, weeksKey } from "./api";
import { orderLaneWithGates } from "./components/laneOrder";
import { readPeriod } from "./components/period";
import { Track } from "./components/Track";
import { formatWeekRange } from "./components/weekLabel";

/**
 * The Listings tab (UX dev spec component M6). Same `/pipeline/week` data as
 * the Pipeline tab — no new API — filtered to the `protex` lane, because the
 * Protex track IS the listings pipeline: p04 discovery through p11 policy,
 * then the `pg2` promote gate (UX §4/§6, cvce-pipeline-3 spec).
 */
export function ListingsTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const navigate = useNavigate();

  const weeksQuery = useQuery({ queryKey: weeksKey, queryFn: getWeeks });
  // Listings has no Week | Month toggle (cvce-pipeline-4 Scope) — it always
  // reads the week grain, but shares the SAME `period` URL param the
  // Pipeline tab now writes (was `week`), so switching the week on either
  // tab still changes it on the other (cvce-pipeline-3 Acceptance 3).
  const requestedWeek = readPeriod(search, "week");
  const defaultWeek = weeksQuery.data?.data.default_week ?? null;
  const weekStart = requestedWeek ?? defaultWeek;

  const weekQuery = useQuery({
    queryKey: weekKey(weekStart ?? "pending"),
    queryFn: () => getWeek(weekStart as string),
    enabled: Boolean(weekStart),
  });

  const setWeek = (next: FilterValues) => {
    const nextSearch: Record<string, unknown> = { ...search, period: next.period };
    delete nextSearch.week;
    void navigate({ to: ".", search: nextSearch, replace: true });
  };
  const openStage = (stageKey: string) => {
    void navigate({ to: ".", search: { ...search, period: weekStart, stage: stageKey }, replace: false });
  };
  const backToPipeline = () => {
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "nucleus", module: params.module ?? "cvce_pipeline", tab: "pipeline" },
      search: { grain: "week", period: weekStart } as never,
    });
  };

  const protexTiles = useMemo(
    () => orderLaneWithGates((weekQuery.data?.data.tiles ?? []).filter((t) => t.lane === "protex")),
    [weekQuery.data],
  );

  if (weeksQuery.isLoading || (Boolean(weekStart) && weekQuery.isLoading)) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p role="status" className="sr-only">
          Loading listings…
        </p>
        {[0, 1].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-ks-md bg-ks-grey-4/40" />
        ))}
      </div>
    );
  }

  if (weeksQuery.isError || weekQuery.isError || weeksQuery.data?.data.available === false) {
    const error = weeksQuery.error ?? weekQuery.error;
    return (
      <AlertCard
        title="We couldn't load listings"
        detail={error ? messageFor(error, areaName) : "Try again, or check back shortly."}
      />
    );
  }

  const weekOptions = weeksQuery.data?.data.weeks ?? [];
  const weekSpec: FilterSpec = {
    name: "period",
    label: "Week",
    kind: "choice",
    options: weekOptions.map((w) => ({ value: w.week_start, label: formatWeekRange(w.week_start) })),
  };

  const week = weekQuery.data?.data ?? null;

  if (!weekStart || !week || week.available === false) {
    return (
      <div className="flex flex-col gap-4">
        <FilterBar
          layout="inline"
          filters={[weekSpec]}
          values={{ period: weekStart ?? "" }}
          defaultValues={{ period: defaultWeek ?? "" }}
          onChange={setWeek}
        />
        <p className="text-sm text-ks-ink">No listings data since we last checked.</p>
      </div>
    );
  }

  const allNoFeed = protexTiles.length > 0 && protexTiles.every((t) => t.grey_reason === "no_feed");

  return (
    <div className="flex flex-col gap-4">
      <FilterBar
        layout="inline"
        filters={[weekSpec]}
        values={{ period: weekStart }}
        defaultValues={{ period: defaultWeek ?? "" }}
        onChange={setWeek}
      />
      <FreshnessLine meta={weeksQuery.data?.meta} pending={weekQuery.isFetching} />

      {allNoFeed && (
        <div className="flex items-center justify-between gap-3 rounded-ks-md border border-ks-grey-4 bg-ks-grey-4/40 p-3">
          <p className="text-sm text-ks-ink">The listings feed isn&apos;t connected yet.</p>
          <button
            type="button"
            data-nonmutating
            onClick={backToPipeline}
            className="shrink-0 text-sm text-ks-ink underline-offset-2 hover:underline"
          >
            Back to Pipeline
          </button>
        </div>
      )}

      <Track title="Protex" tiles={protexTiles} onOpen={openStage} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ks-grey-1">First-pass approval</p>
          <p className="text-sm text-ks-ink">Listings data isn&apos;t connected yet (Protex feed).</p>
        </div>
        <div className="flex flex-col gap-1 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ks-grey-1">Packs per channel</p>
          <p className="text-sm text-ks-ink">Listings data isn&apos;t connected yet (Protex feed).</p>
        </div>
      </div>
    </div>
  );
}
